import type { Extent } from 'ol/extent';
import { createEmpty, extend } from 'ol/extent';
import type { AppState, VectorFeatureRecord } from '../core/types';
import { layerClassificationAssignments } from '../domain/classification';
import { masterThemeAssignments } from '../presentation/masterTheme';
import { deriveDisplaySet } from '../domain/displaySet';
import { recordsForSelection } from '../domain/selectionSet';
import { visibleThemeTargetIds } from '../domain/themeTarget';

export type MapExportContent = 'map-legend' | 'map' | 'legend';
export type MapExportScope = 'visible-polygons' | 'selection' | 'current-view';
export type MapExportBackground = 'basemap' | 'white' | 'transparent';
export type MapExportResolution = 'normal' | 'high' | 'very-high';

export interface MapExportOptions {
  readonly content: MapExportContent;
  readonly scope: MapExportScope;
  readonly marginPercent: number;
  readonly resolution: MapExportResolution;
  readonly background: MapExportBackground;
  readonly showLabels: boolean;
  readonly showLegend: boolean;
  readonly showScale: boolean;
  readonly showNorth: boolean;
  readonly showBasemap: boolean;
}

export const exportWidths: Readonly<Record<MapExportResolution, number>> = {
  normal: 1920,
  high: 3000,
  'very-high': 4000,
};

const isPolygonal = (type: string | undefined): boolean => type === 'Polygon' || type === 'MultiPolygon';

/**
 * Smallest useful frame (map units ≈ metres) for compositions WITHOUT polygons: one hydrant, or a few close points, would
 * otherwise be framed at an absurd zoom. Compositions with polygons keep their exact extent (Maestro unchanged).
 */
export const MIN_POINT_LINE_EXPORT_SPAN = 300;

/** Extent of every geometry (points, lines and polygons): a proposal can be only hydrants and pipes. */
const extentOf = (records: readonly VectorFeatureRecord[]): Extent | null => {
  const extent = createEmpty(); let polygonal = false;
  records.forEach(({ feature }) => {
    const geometry = feature.getGeometry();
    if (!geometry) return;
    const geometryExtent = geometry.getExtent(); if (!geometryExtent.every(Number.isFinite)) return;
    extend(extent, geometryExtent); polygonal ||= isPolygonal(geometry.getType());
  });
  if (!extent.every(Number.isFinite) || extent[0]! > extent[2]! || extent[1]! > extent[3]!) return null;
  return polygonal ? extent : withMinimumSpan(extent, MIN_POINT_LINE_EXPORT_SPAN);
};

/** Grows each axis of the extent around its centre up to `span`. */
export function withMinimumSpan(extent: Extent, span: number): Extent {
  const [minX = 0, minY = 0, maxX = 0, maxY = 0] = extent;
  const grow = (min: number, max: number): [number, number] => max - min >= span ? [min, max] : [(min + max) / 2 - span / 2, (min + max) / 2 + span / 2];
  const [x0, x1] = grow(minX, maxX); const [y0, y1] = grow(minY, maxY);
  return [x0, y0, x1, y1];
}

const exportableLayers = (state: AppState) => state.layers.filter(({ visible, opacity }) => visible && opacity > 0);

/**
 * Uses the same DISPLAY SET as the interactive map and includes every visible vector layer and every geometry type
 * (Maestro polygons, external layers, planning hydrants and pipes). Hidden layers and filtered objects stay out.
 */
export function visibleObjectsExtent(state: AppState): Extent | null {
  const display = deriveDisplaySet(state);
  const records = exportableLayers(state).flatMap((layer) => {
    const ids = display.layerFeatureIds.get(layer.id) ?? new Set(layer.features.map(({ id }) => id));
    const available = layer.kind === 'master' && state.model
      ? state.model.fragments.map(({ id, feature, attributes }) => ({ id, feature, attributes }))
      : layer.features;
    const displayed = available.filter(({ id }) => ids.has(id));
    const config = state.classificationConfig;
    const thematic = config.layerId === layer.id && (config.mode === 'single' || Boolean(config.field));
    if (!thematic) return displayed;
    const theme = layer.kind === 'master' ? masterThemeAssignments(state, layer) : null;
    const target = theme?.subsetIds ?? visibleThemeTargetIds(state, layer);
    const assignmentSet = config.field
      ? theme?.assignments ?? layerClassificationAssignments(layer, config.field, target, config)
      : null;
    const categories = new Map(config.categories.map((category) => [category.value, category]));
    return displayed.filter(({ id }) => {
      if (!target.has(id)) return config.thematicView !== 'classified-only';
      const pieces = theme?.piecesBySuerteId.get(state.model?.fragmentsById.get(id)?.suerteId ?? '');
      if (pieces) return pieces.some((piece) => (categories.get(piece.category)?.visible ?? true) && (config.showNoValue || piece.hasValue));
      if (config.field && !config.showNoValue && assignmentSet?.hasValueByFragmentId.get(id) === false) return false;
      const category = categories.get(assignmentSet?.categoryByFragmentId.get(id) ?? '');
      return !category || category.visible;
    });
  });
  return extentOf(records);
}

/** Selected objects of a visible layer (a single click on a planning object counts as a selection of one). */
export function selectionExtent(state: AppState): Extent | null {
  const generic = state.genericSelection;
  const selectionSet = state.selectionSet.entries.length || !generic
    ? state.selectionSet
    : { layerId: generic.layerId, entries: [{ source: 'generic' as const, layerId: generic.layerId, kind: 'feature' as const, id: generic.featureId }], focus: null };
  const layer = exportableLayers(state).find(({ id }) => id === selectionSet.layerId);
  if (!layer) return null;
  const display = layer.kind === 'master' ? null : deriveDisplaySet(state).layerFeatureIds.get(layer.id);
  return extentOf(recordsForSelection(layer, selectionSet).filter(({ id }) => !display || display.has(id)));
}

export function extentForExport(state: AppState, scope: MapExportScope, current: Extent): Extent | null {
  if (scope === 'current-view') return [...current];
  return scope === 'selection' ? selectionExtent(state) : visibleObjectsExtent(state);
}

export function expandExtentByMargin(extent: Extent, percent: number): Extent {
  const safe = Math.max(0, Math.min(100, percent)) / 100;
  const [minX = 0, minY = 0, maxX = 0, maxY = 0] = extent;
  const width = Math.max(maxX - minX, 1);
  const height = Math.max(maxY - minY, 1);
  const x = width * safe; const y = height * safe;
  return [minX - x, minY - y, maxX + x, maxY + y];
}

export function exportPixelSize(width: number, currentSize: readonly [number, number]): [number, number] {
  const ratio = currentSize[0] > 0 && currentSize[1] > 0 ? currentSize[1] / currentSize[0] : 9 / 16;
  return [width, Math.max(1, Math.round(width * ratio))];
}

export function sanitizeFilenamePart(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').replace(/_+/g, '_');
}

export function suggestedExportFilename(state: AppState, content: MapExportContent, scope: MapExportScope): string {
  const date = new Date().toISOString().slice(0, 10);
  const theme = sanitizeFilenamePart(state.classificationConfig.name || state.classificationConfig.field || 'Mapa');
  if (content === 'legend') return `GeoStudio_Leyenda_${theme}_${date}.png`;
  if (scope === 'selection') return `GeoStudio_Seleccion_${date}.png`;
  const fincaNames = state.workScope.fincaIds.flatMap((id) => {
    const name = state.model?.fincas.get(id)?.name;
    return name ? [sanitizeFilenamePart(name)] : [];
  });
  const subject = fincaNames.length === 1 ? fincaNames[0]! : scope === 'current-view' ? 'Vista_actual' : 'Objetos_visibles';
  return `GeoStudio_${subject}${theme && theme !== 'Sin_clasificacion' ? `_${theme}` : ''}_${date}.png`;
}

export class CanvasExportSecurityError extends Error {
  constructor() { super('El mapa base actual impide exportar por restricciones CORS. Exporta sin mapa base o cambia a un mapa base compatible.'); }
}

export function assertCanvasExportable(canvas: HTMLCanvasElement): void {
  try { canvas.getContext('2d')?.getImageData(0, 0, 1, 1); }
  catch { throw new CanvasExportSecurityError(); }
}
